import React, { useRef, useState } from "react";

/**
 * ChipsInput
 * - Allows multiple email entry as chips.
 * - On blur or Enter, validates and adds as chip.
 * - Calls onChange with array of emails.
 * - inputType: email || text 
 */
export default function ChipsInput({ value = [], onChange, placeholder = "Email Address", inputType = "email", maxLength }) {
  const [input, setInput] = useState("");
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  const isValidEmail = (email) =>
    /^\S+@\S+\.\S+$/.test(email.trim());

  // For mobile validation: only digits, 10-12 digits, formatted as 000 - 000 - 0000
  const isValidMobile = (mobile) => {
    const digits = mobile.replace(/[^0-9]/g, "");
    return digits.length >= 10 && digits.length <= 12;
  };

  // Format mobile as 000 - 000 - 0000 while typing
  const formatMobile = (input) => {
    const digits = input.replace(/[^0-9]/g, "").slice(0, 20);
    let formatted = "";
    for (let i = 0; i < digits.length; i++) {
      if (i === 3 || i === 6) formatted += " - ";
      formatted += digits[i];
    }
    return formatted;
  };

  const addEmail = (email) => {
    setError('');
    const trimmed = email.trim();
    if (!trimmed) return;
    if (inputType === 'email') {
      if (isValidEmail(trimmed) && !value.includes(trimmed)) {
        onChange([...value, trimmed]);
        setInput("");
        return;
      }
      setError('Please check and enter valid emails!');
    } else if (inputType === 'text') {
      const formatted = formatMobile(trimmed);
      if (isValidMobile(trimmed) && !value.includes(formatted)) {
        onChange([...value, formatted]);
        setInput("");
        return;
      }
      setError('Please enter a valid phone number!');
    }
  };

  const handleBlur = () => { addEmail(input) };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addEmail(input);
    } else if (e.key === "Backspace" && !input && value.length) {
      // Remove last chip
      onChange(value.slice(0, -1));
    }
  };

  const removeChip = (idx) => {
    onChange(value.filter((_, i) => i !== idx));
  };

  return (
    <>
        <div
            style={{
                minHeight: 48,
                border: "1px solid var(--color-heading)",
                borderRadius: 8,
                padding: 8,
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: 6,
                background: "#fff"
            }}
            onClick={() => inputRef.current && inputRef.current.focus()}
        >
        {value.map((email, idx) => (
            <span
            key={idx}
            style={{ display: "flex", alignItems: "center", background: "#e0e0e0", borderRadius: 16, padding: "4px 10px", margin: "4px 4px 4px 0", fontSize: 14, height: "25px" }}
            >
            {email}
            <button type="button" aria-label="Remove" 
                onClick={(e) => { e.stopPropagation(); removeChip(idx); }} 
                style={{
                    background: "none",
                    border: "none",
                    marginLeft: 10,
                    cursor: "pointer",
                    fontWeight: "bold",
                    fontSize: 16,
                    color: "#444"
                }}
            >
                ×
            </button>
            </span>
        ))}
        <input
            ref={inputRef}
            type={inputType}
            style={{
                border: "none",
                outline: "none",
                flex: 1,
                minWidth: 120,
                fontSize: 15,
                background: "transparent",
                padding: 4
            }}
            value={input}
            onChange={(e) => {
              let val = e.target.value;
              if (inputType === 'text') {
                val = formatMobile(val);
              }
              setInput(val);
            }}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            placeholder={value.length === 0 ? placeholder : ""}
            autoComplete="off"
            maxLength={maxLength || (inputType === 'text' ? 17 : undefined)}
        />
        </div>
      {error && <p style={{ color: "red", fontSize: 12 , marginTop: 8}}>{error}</p>}
    </>
  );
}
